/**
 * HighlightedText — wraps every case-insensitive occurrence of `query` in
 * `text` with a <mark> tag. Used by Operational Base Review Shield
 * Intelligence + classic Review Shield Results to surface why a record
 * matched a search query.
 *
 * Defensive:
 *   - Empty / whitespace-only query falls through to plain text.
 *   - Null / undefined text falls through to the `fallback`.
 *   - Special regex characters in the query are escaped.
 *   - Stable React keys derived from the split positions, never the index.
 *
 * Visual:
 *   - <mark> is styled via Tailwind classes (yellow in light, amber in dark)
 *     so it works in both themes without extra CSS.
 *
 * v4.34.5 — added as part of the cross-page Operational Base search rollout.
 */
import React from 'react';

const escapeRegExp = (raw) => String(raw).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const HighlightedText = ({
    text,
    query,
    fallback = null,
    className = '',
    testId,
}) => {
    if (text === null || text === undefined || text === '') {
        return fallback;
    }

    const trimmed = (query || '').trim();
    if (!trimmed) {
        return <span className={className} data-testid={testId}>{text}</span>;
    }

    const pattern = new RegExp(`(${escapeRegExp(trimmed)})`, 'gi');
    const segments = String(text).split(pattern);

    return (
        <span className={className} data-testid={testId}>
            {segments.map((segment, idx) => {
                if (!segment) return null;
                const isMatch = pattern.test(segment);
                // Reset lastIndex because we used a global pattern in .test()
                pattern.lastIndex = 0;
                if (isMatch) {
                    return (
                        <mark
                            // Index is the right key here — segment positions
                            // are stable for a given (text, query) pair.
                            // eslint-disable-next-line react/no-array-index-key
                            key={`hl-${idx}`}
                            className="rounded px-0.5 bg-yellow-200/90 text-slate-900 dark:bg-amber-400/40 dark:text-amber-50"
                            data-testid="hl-match"
                        >
                            {segment}
                        </mark>
                    );
                }
                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={`hl-${idx}`}>{segment}</React.Fragment>
                );
            })}
        </span>
    );
};

export default HighlightedText;
